// Super Admin — Legal Pages CMS
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileText, Save, RotateCcw, Eye, Calendar } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";

const PAGES = [
  { type: "privacy_policy",   title: "Privacy Policy",     slug: "privacy-policy" },
  { type: "terms_conditions", title: "Terms & Conditions", slug: "terms-conditions" },
  { type: "refund_policy",    title: "Refund Policy",      slug: "refund-policy" },
  { type: "cookies_policy",   title: "Cookies Policy",     slug: "cookies-policy" },
];

function formatDate(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso;
}

export function AdminLegalPagesPage() {
  const [rows, setRows] = useState(null);
  const [active, setActive] = useState("privacy_policy");
  const [drafts, setDrafts] = useState({}); // page_type -> {title, content_md, last_updated_date, status}
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/legal-pages");
      setRows(data.data?.pages || []);
      const d = {};
      (data.data?.pages || []).forEach((p) => {
        d[p.page_type] = {
          title: p.title,
          content_md: p.content_md,
          last_updated_date: p.last_updated_date,
          status: p.status,
        };
      });
      setDrafts(d);
    } catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const current = rows?.find((r) => r.page_type === active);
  const draft = drafts[active];

  const update = (field, value) => {
    setDrafts((d) => ({ ...d, [active]: { ...d[active], [field]: value } }));
  };

  const save = async () => {
    if (!draft) return;
    if (draft.last_updated_date && !/^\d{4}-\d{2}-\d{2}$/.test(draft.last_updated_date)) {
      return toast.error("Last updated date must be YYYY-MM-DD");
    }
    setBusy(true);
    try {
      await api.put(`/admin/legal-pages/${active}`, {
        title: draft.title,
        content_md: draft.content_md,
        last_updated_date: draft.last_updated_date,
        status: draft.status,
      });
      toast.success(`${current?.title || "Page"} saved`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm(`Restore platform default for "${current?.title}"?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/legal-pages/reset/${active}`);
      toast.success("Restored to default");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const dirty = useMemo(() => {
    if (!current || !draft) return false;
    return (
      draft.title !== current.title ||
      draft.content_md !== current.content_md ||
      draft.last_updated_date !== current.last_updated_date ||
      draft.status !== current.status
    );
  }, [current, draft]);

  if (rows === null) return <DashboardLayout title="Legal Pages"><LoadingBlock /></DashboardLayout>;

  return (
    <DashboardLayout title="Legal Pages CMS" breadcrumbs={["Admin", "Master", "Legal Pages"]}>
      <div className="space-y-4">
        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2" data-testid="legal-tabs">
          {PAGES.map((p) => {
            const isActive = active === p.type;
            const row = rows.find((r) => r.page_type === p.type);
            return (
              <button
                key={p.type}
                onClick={() => { setActive(p.type); setPreview(false); }}
                data-testid={`legal-tab-${p.type}`}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-bold transition ${isActive ? "border-[#101876] bg-[#101876] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
              >
                <FileText className="h-3 w-3" />
                {p.title}
                {row?.status && (
                  <span className={`rounded-full px-1.5 text-[10px] ${isActive ? "bg-white/20" : row.status === "published" ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {row.status}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {draft && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="legal-editor">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
              <div>
                <div className="text-sm font-black text-slate-900">{current?.title}</div>
                <div className="text-[11px] text-slate-500">
                  Public URL: <a href={`/legal/${current?.slug}`} target="_blank" rel="noreferrer" className="text-[#101876] hover:underline">/legal/{current?.slug}</a>
                  {" · "}Displayed as: <span className="font-mono">Last Updated: {formatDate(draft.last_updated_date)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPreview((p) => !p)}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid="legal-toggle-preview"
                >
                  <Eye className="h-3 w-3" />{preview ? "Edit" : "Preview"}
                </button>
                <button
                  onClick={reset}
                  disabled={busy}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  data-testid="legal-reset"
                >
                  <RotateCcw className="h-3 w-3" />Restore default
                </button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-bold text-slate-700">Title</label>
                <input
                  value={draft.title}
                  onChange={(e) => update("title", e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20"
                  data-testid="legal-title-input"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold text-slate-700">
                  <Calendar className="mr-1 inline h-3 w-3" />Last Updated Date
                </label>
                <input
                  type="date"
                  value={draft.last_updated_date || ""}
                  onChange={(e) => update("last_updated_date", e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20"
                  data-testid="legal-date-input"
                />
                <div className="mt-1 text-[10px] text-slate-500">Public shows: {formatDate(draft.last_updated_date)}</div>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-4 text-sm">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  checked={draft.status === "published"}
                  onChange={() => update("status", "published")}
                  data-testid="legal-status-published"
                />
                Published
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  checked={draft.status === "draft"}
                  onChange={() => update("status", "draft")}
                  data-testid="legal-status-draft"
                />
                Draft (hidden from public)
              </label>
            </div>

            <div className="mt-4">
              <label className="mb-1 block text-xs font-bold text-slate-700">
                Content (Markdown){" "}
                <span className="font-normal text-slate-400">
                  · headings, bold, tables and lists supported
                </span>
              </label>
              {preview ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2 text-sm text-slate-700" data-testid="legal-preview">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: () => null,
                      h2: ({node, ...p}) => <h2 className="mt-4 text-lg font-black text-slate-900" {...p} />,
                      h3: ({node, ...p}) => <h3 className="mt-3 text-sm font-bold text-slate-900" {...p} />,
                      p:  ({node, ...p}) => <p className="text-sm text-slate-700" {...p} />,
                      ul: ({node, ...p}) => <ul className="ml-5 list-disc space-y-1" {...p} />,
                      ol: ({node, ...p}) => <ol className="ml-5 list-decimal space-y-1" {...p} />,
                      a:  ({node, ...p}) => <a className="text-[#101876] hover:underline" {...p} />,
                      table: ({node, ...p}) => <div className="my-3 overflow-x-auto"><table className="w-full border-collapse text-xs" {...p} /></div>,
                      th: ({node, ...p}) => <th className="border border-slate-200 bg-white px-2 py-1 text-left font-semibold" {...p} />,
                      td: ({node, ...p}) => <td className="border border-slate-200 px-2 py-1 align-top" {...p} />,
                    }}
                  >
                    {draft.content_md}
                  </ReactMarkdown>
                </div>
              ) : (
                <textarea
                  value={draft.content_md}
                  onChange={(e) => update("content_md", e.target.value)}
                  rows={22}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 font-mono text-xs focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20"
                  data-testid="legal-content-input"
                />
              )}
            </div>

            <div className="mt-4 flex items-center justify-end gap-3">
              {dirty && <span className="text-xs font-semibold text-amber-600" data-testid="legal-dirty">Unsaved changes</span>}
              <button
                onClick={save}
                disabled={busy || !dirty}
                className="inline-flex items-center gap-2 rounded-xl bg-[#101876] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0d1360] disabled:opacity-50"
                data-testid="legal-save"
              >
                <Save className="h-4 w-4" />{busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
